import { PurchaseOrderQuery, PurchaseOrderQueryV2 } from 'src/WebApiController';

type CatalogEventId = {
  viagVirtId: string;
  performerId: number | null;
  venueId: number;
};

/**
 * Builds the filter query for V2 purchase order search/summary requests.
 *
 * Only `eventOrMappingIds` falls back to the catalog event window (`allEventIds`)
 * when the user has no explicit selection — this keeps V2 scoped to the same
 * catalog window as V1 without over-constraining results.
 *
 * `performerIds` and `venueIds` must NOT fall back to `allEventIds`.  The catalog
 * counts API returns POS-internal IDs (e.g. negative virtual venue IDs like
 * `-1455734` for unmapped events), but OpenSearch stores `CatalogData.VenueId = 0`
 * and `CatalogData.PerformerId = null` for those same unmapped events.  Passing the
 * POS-internal IDs as AND filters to the QB would incorrectly exclude POs whose OS
 * documents carry `0`/`null` for those fields, causing V1/V2 count mismatches.
 * The `eventOrMappingIds` filter alone is sufficient to scope results correctly.
 *
 * @param moddedFilterQuery - The base filter query (with eventOrMappingIds /
 *   performerIds / venueIds already nulled-out by the hook's `useMemo`).
 * @param filterQuery - The raw filter from the FilterQueryContext (carries
 *   explicit user selections, or null when the user hasn't filtered).
 * @param allEventIds - The catalog event window provided by CatalogDataContext.
 */
export function buildV2FilterQuery(
  moddedFilterQuery: Partial<PurchaseOrderQuery | PurchaseOrderQueryV2>,
  filterQuery: Pick<
    PurchaseOrderQuery | PurchaseOrderQueryV2,
    'eventOrMappingIds' | 'performerIds' | 'venueIds'
  >,
  allEventIds: CatalogEventId[] | null | undefined
): Partial<PurchaseOrderQueryV2> {
  return {
    ...moddedFilterQuery,
    eventOrMappingIds:
      filterQuery.eventOrMappingIds ??
      (allEventIds ?? []).map((ev) => ev.viagVirtId),
    performerIds: filterQuery.performerIds ?? null,
    venueIds: filterQuery.venueIds ?? null,
  };
}
